import { useState } from "react";
import { BibleVerseInput, BibleVersesApi } from "./bible-verse-api";
import { BibleVerse, getCategoryDisplayName, getLanguageDisplayName, Language, VerseCategory } from "./types";

const bibleVerseApi = new BibleVersesApi()

export const BibleVersEdit = ({ isCreate, onClose, verse }: { isCreate: boolean, onClose: () => void, verse: BibleVerse | null }) => {

    const [formData, setFormData] = useState<BibleVerseInput>((isCreate || !verse) ? {
        reference: '',
        verse_text: '',
        language: Language.ENGLISH,
        category: VerseCategory.INSPIRATIONAL,
        description: '',
        author: '',
        notes: ''
    } : verse!);

    const [selectedVerse, setSelectedVerse] = useState<BibleVerse | null>(verse);
    const [isEditing, setIsEditing] = useState(!isCreate);
    const [isCreating, setIsCreating] = useState(isCreate);
    const [verses, setVerses] = useState<BibleVerse[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleFormChange = (field: keyof BibleVerseInput, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const validateInput = () => {
        if (formData.reference == '')
            alert("Reference cannot be blank")
        else if (formData.verse_text == '')
            alert("Content cannot be blank")
        else if (!formData.category)
            alert("Choose a category")
        else if (!formData.language)
            alert("Choose a language")

    }

    const handleCreateVerse = async () => {
        try {
            const newVerse: BibleVerse = {
                id: verses.length + 1,
                ...formData,
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date(),
                is_default: false
            };
            setVerses(prev => [...prev, newVerse]);
            validateInput()
            bibleVerseApi.createVerse(formData).then((response) => {
                if (response.status == "ok")
                    onClose()
                else if (response.status == "error")
                    alert(response.error.message)
                else
                    alert("Error")

            }).catch(e => {
                alert(e)
            })


        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    const handleUpdateVerse = async () => {
        if (!selectedVerse) return;

        try {
            const updatedVerse: BibleVerse = {
                ...selectedVerse,
                ...formData,
                updatedAt: new Date()
            };
            setVerses(prev => prev.map(v => v.id === selectedVerse.id ? updatedVerse : v));
            setIsEditing(false);
            setSelectedVerse(null);
            const newVerse: BibleVerse = {
                id: verses.length + 1,
                ...formData,
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date(),
                //TODO handle correctly
                is_default: false
            };
            setVerses(prev => [...prev, newVerse]);
            validateInput()

            bibleVerseApi.updateVerse(updatedVerse).then((response) => {
                if (response.status == "ok")
                    onClose()
                else if (response.status == "error")
                    alert(response.error.message)
                else
                    alert("Error")

            }).catch(e => {
                alert(e)
            })

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };
    return (
        <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full p-8 relative mt-8 mb-8 overflow-y-scroll">

                <div className="p-6">
                    <h2 className="text-xl font-semibold text-black dark:text-white mb-4">
                        {isCreating ? 'Add Verse' : 'Edit Verse'}
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Reference *
                            </label>
                            <input
                                type="text"
                                value={formData.reference}
                                onChange={(e) => handleFormChange('reference', e.target.value)}
                                placeholder="e.g., John 3:16"
                                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Content *
                            </label>
                            <textarea
                                value={formData.verse_text}
                                onChange={(e) => handleFormChange('verse_text', e.target.value)}
                                placeholder="Enter the verse content..."
                                rows={4}
                                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Category *
                                </label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => handleFormChange('category', e.target.value as VerseCategory)}
                                    className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                                >
                                    {Object.values(VerseCategory).map(category => (
                                        <option key={category} value={category}>
                                            {getCategoryDisplayName(category)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Language *
                                </label>
                                <select
                                    value={formData.language}
                                    onChange={(e) => handleFormChange('language', e.target.value as Language)}
                                    className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                                >
                                    {Object.values(Language).map(language => (
                                        <option key={language} value={language}>
                                            {getLanguageDisplayName(language)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleFormChange('description', e.target.value)}
                                placeholder="Optional description or context..."
                                rows={2}
                                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Author
                            </label>
                            <input
                                type="text"
                                value={formData.author}
                                onChange={(e) => handleFormChange('author', e.target.value)}
                                placeholder="e.g., Paul, David, John"
                                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                            />
                        </div>
                        <div className="flex space-x-4 pt-4 justify-end">
                            <button
                                onClick={isCreating ? handleCreateVerse : handleUpdateVerse}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
                            >
                                {isCreating ? 'Add Verse' : 'Update Verse'}
                            </button>
                            <button
                                onClick={() => {
                                    setIsCreating(false);
                                    setIsEditing(false);
                                    setSelectedVerse(null);
                                    onClose()
                                }}
                                className="px-4 py-2 bg-neutral-500 text-white rounded-lg hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>)
}